/**
 * client/src/App.tsx — the shell and the route table.
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * THE ONE STRUCTURAL RULE: A SESSION IS ONE ROUTE
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * `<Studio>` owns everything from the name question to the payoff — name, permission, room check,
 * every prompt, the wait, the payoff — inside **one** route, and that is not an accident of how it
 * was written. A client-side route change unmounts the tree, and the tree holds the `AudioContext`,
 * the `AudioWorklet` node, the open `MediaStreamTrack` and the encode worker. Unmounting any of them
 * mid-session is VOX-LAW 1's failure with no error anywhere: the capture stops, the take is lost, and
 * the person in front of it is told nothing because VOX-LAW 16b forbids telling her.
 *
 * So the table below has two kinds of route and they must not be confused:
 *
 *   · **The live arc — `/make`.** One route, one mount, microphone open. Nothing navigates inside it.
 *   · **The re-entrant screens — `/s/:sessionId/…`, `/back/:claimToken`.** The same screens, reached
 *     by URL, minutes or weeks later, from a browser that may never have seen this site. No
 *     microphone, no session state in memory, everything resolved from the server. These exist
 *     because VOX-LAW 20 says the durable handle is a claim token and not a cookie — and a durable
 *     handle that only renders inside the tab that minted it is not durable.
 *
 * `<Wait>` and `<Reveal>` are therefore not duplicates of the studio's own waiting and payoff steps.
 * They are the versions that work when the tab is gone: the screen a grandchild opens on a phone
 * three weeks later holding a link, which the studio component structurally cannot serve because it
 * begins by creating a session.
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * THE THREE-STATE RULE, WHICH THIS TABLE APPLIES EXACTLY ONCE
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * `loading` is not "signed out". Rendering a face while `GET /api/v1/session` is in flight flashes
 * the wrong page at somebody on *every single load* (books.karti.ai's scar, quoted in audiobook's
 * router and repeated here because it is about to be repeated in the same place).
 *
 * ⚠️ But the door is the exception, and deliberately so: `<Landing>` renders immediately and gates
 * only its own second card, because nothing on the primary path depends on who is asking. See its
 * header. `<Gate>` below is for the screens where the answer changes what exists.
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * VOX-LAW 16b, AT THE TOP
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * `<FamilyBoundary>` wraps the whole family path. A render throw three components down is answered by
 * React by unmounting everything and painting white — which on this product is indistinguishable from
 * "the computer broke" and has no next step. One warm sentence and one control instead.
 *
 * The console is deliberately NOT inside it. On `/live` and `/status` a white page with a stack in
 * the devtools is a better outcome than a warm sentence, because the reader is the person who can act
 * on it.
 */

import { Suspense, lazy } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import type { ReactNode } from "react";
import { FAMILY } from "../../shared/voice/copy.ts";
import { useSession } from "./lib/session.tsx";
import { Claim } from "./pages/Claim.tsx";
import { Kept } from "./pages/Kept.tsx";
import { Landing } from "./pages/Landing.tsx";
import { Live } from "./pages/Live.tsx";
import { Reveal } from "./pages/Reveal.tsx";
import { SignIn } from "./pages/SignIn.tsx";
import { Status } from "./pages/Status.tsx";
import { Wait } from "./pages/Wait.tsx";
import { Blank, FamilyBoundary, LinkButton } from "./ui/primitives.tsx";

/**
 * The studio is the only lazy chunk in this client, and the reason is measured in bytes rather than
 * taste: it drags in the worklet loader, the encode worker's module graph and the meter. The door,
 * the claim screen and the console must not pay for that — `/back/:claimToken` in particular is
 * opened on a phone, on mobile data, by somebody who is not going to record anything.
 *
 * `<Suspense>` falls back to `<Blank>` and not to a spinner: the chunk is served from the same origin
 * that just served the document, so the gap is a few milliseconds and a spinner in it is a flash of
 * anxiety with no information in it.
 */
const Studio = lazy(() => import("./studio/Studio.tsx").then((m) => ({ default: m.Studio })));
const Admin = lazy(() => import("./pages/Admin.tsx").then((m) => ({ default: m.Admin })));
const Voices = lazy(() => import("./pages/Voices.tsx").then((m) => ({ default: m.Voices })));
const Voice = lazy(() => import("./pages/Voice.tsx").then((m) => ({ default: m.Voice })));

export function App() {
  return (
    <BrowserRouter>
      <AppRoutes />
    </BrowserRouter>
  );
}

/**
 * Everything behind a signed-in account.
 *
 * ⚠️ A *routing* convenience, never an authorisation. Every screen behind it is enforced again on the
 * server, and every byte route goes through `readAccess` refusing 404 (VOX-LAW 18). The client's
 * answer is a courtesy; the server's is the authority.
 */
function Gate({ children }: { children: ReactNode }) {
  const { signedIn, loading } = useSession();
  const location = useLocation();
  if (loading) return <Blank />;
  if (!signedIn) return <Navigate to="/signin" state={{ from: location.pathname + location.search }} replace />;
  return <>{children}</>;
}

function AppRoutes() {
  return (
    <Routes>
      {/* ── the family path ────────────────────────────────────────────── */}
      <Route
        path="/"
        element={
          <Family>
            <Landing />
          </Family>
        }
      />

      {/* The live arc. One route, one mount, microphone open. See the header. */}
      <Route
        path="/make"
        element={
          <Family>
            <Suspense fallback={<Blank />}>
              <StudioRoute />
            </Suspense>
          </Family>
        }
      />

      {/*
        The re-entrant screens.

        `/s/:sessionId` and not `/make/:sessionId`: "make" is a verb about something happening now,
        and these are the screens for a session that already happened. It also keeps the studio's
        route free of a parameter it does not take.

        ⚠️ **`/s/` is a client route and is NOT proxied** — `vite.config.ts` proxies only `^/c/`,
        `/api`, `/ws` and `/health`. That is the audiobook scar in its original form: a bare `"/s"`
        key in a proxy table is a PREFIX match and it captured `/src/main.tsx`, the dev server's own
        entry module. If you ever add `/s` to that table, anchor it.
      */}
      <Route
        path="/s/:sessionId/wait"
        element={
          <Family>
            <Wait />
          </Family>
        }
      />
      <Route
        path="/s/:sessionId/hear"
        element={
          <Family>
            <Reveal />
          </Family>
        }
      />
      {/*
        Where "Keep it" lands, and it is a FAMILY route on purpose.
        §3.13's ordering ends with the offer to save it, and the offer used to end on `/voices` —
        Karti's console, in 11 px capitals, saying UNCLAIMED and `no rev` to the person who had just
        been told the voice was hers. `<Kept>` is the ending that sentence promised; `/voices` and
        `/voices/:id` stay behind `<Gate>` as the console they are.
      */}
      <Route
        path="/s/:sessionId/kept"
        element={
          <Family>
            <Kept />
          </Family>
        }
      />

      {/*
        VOX-LAW 20's durable handle, twice.

        `/c/:claimToken` is a SERVER route: it resolves the token, decides what the holder may see,
        and redirects to `/back/:claimToken`. It is registered here as well, and that is
        belt-and-braces rather than duplication — without it, a lost proxy entry or a static preview
        build turns somebody's only handle to a recording into the SPA fallback rendering the front
        door. `<Claim>` resolves the token itself, so it is correct at either path.
      */}
      <Route
        path="/back/:claimToken"
        element={
          <Family>
            <Claim />
          </Family>
        }
      />
      <Route
        path="/c/:claimToken"
        element={
          <Family>
            <Claim />
          </Family>
        }
      />

      {/* ── the console ────────────────────────────────────────────────── */}

      {/*
        `/live/:sessionId` is NOT gated, and that is §3.15 rather than an oversight: it "is offered
        as a link she can be told to send him", and the helper is frequently a person with no account
        on a phone in another city. `readAccess` on the SSE route is what decides, and it refuses 404.
        A client-side sign-in wall here would break the one screen that exists to end support calls.
      */}
      <Route path="/live/:sessionId" element={<Live />} />

      <Route path="/signin" element={<SignInRoute />} />

      <Route
        path="/voices"
        element={
          <Gate>
            <Suspense fallback={<Blank />}>
              <Voices />
            </Suspense>
          </Gate>
        }
      />
      <Route
        path="/voices/:voiceId"
        element={
          <Gate>
            <Suspense fallback={<Blank />}>
              <Voice />
            </Suspense>
          </Gate>
        }
      />

      {/*
        `/status` is not wrapped in `<Gate>` either, for the reason the sibling repos give about
        `/admin`: a gate bounces a stranger to /signin, which tells them the page is real. It decides
        for itself below and sends everyone else home.
      */}
      <Route path="/status" element={<StatusRoute />} />
      <Route path="/admin/*" element={<AdminRoute />} />

      {/* Anything else goes home rather than to a blank page. */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

/**
 * The family path's boundary and its one escape hatch.
 *
 * The action is a link to the door and not a "reload" button: reloading re-runs the same code that
 * just threw, and pressing a button that appears to do nothing is how somebody concludes the whole
 * machine is broken. `FAMILY.door.primary` is the one control on the door, so the escape is the
 * beginning rather than a retry.
 */
function Family({ children }: { children: ReactNode }) {
  return (
    <FamilyBoundary
      sentence={FAMILY.mishap.keepGoing}
      action={
        <div className="vox-btn-row">
          <LinkButton to="/" variant="primary">
            {FAMILY.door.primary}
          </LinkButton>
        </div>
      }
    >
      {children}
    </FamilyBoundary>
  );
}

/**
 * The studio's four props, from the session envelope.
 *
 * None of them is `import.meta.env`. `ttsConfigured` in particular must arrive over the wire: it is
 * `false` under `VOX_TTS_MODE=none`, which is the correct setting today, and a build-time flag would
 * be baked into a bundle that outlives the outage by weeks — so the studio would offer the deferred
 * choice on a day the GPU was up, or worse, not offer it on a day it was down.
 *
 * `revealText` is deliberately not passed. §3.13's sentence is authored by the other person and "she
 * never sees it"; there is no screen in P1 on which he types it, and inventing one on the family path
 * would put it in front of her. **The law and the reason it is deferred:** it ships in P3 with the
 * payoff it feeds, on the helper's own screen, because a column nothing reads is not worth an input.
 *
 * `onLongerSession` is not passed either — §15 ships the long session as a post-reveal upsell in
 * **P6**, and the studio's own contract says an absent handler means the offer is not rendered,
 * "because a button that says *'yes, a few more'* and then leaves is worse than a sentence nobody
 * read."
 */
function StudioRoute() {
  const { app, loading, signedIn } = useSession();
  const navigate = useNavigate();
  if (loading || !app) return <Blank />;
  return (
    <Studio
      helperName={app.helperName}
      ttsConfigured={app.ttsConfigured}
      publicOrigin={app.publicOrigin}
      onLeave={() => navigate("/", { replace: true })}
      onSignIn={
        signedIn
          ? undefined
          : (claimToken: string) =>
              navigate("/signin", { state: { from: `/s/${encodeURIComponent(claimToken)}/kept` } })
      }
    />
  );
}

/**
 * Never flash the door at somebody who already has a key, and never the console at somebody who does
 * not.
 *
 * ⚠️ **It honours `state.from`, and not honouring it is how the family path ended on `/voices`.**
 * Everything that sends somebody here carries where they came from — `<Gate>`, `<Reveal>`'s "Keep it",
 * the studio's own — and this component is the last thing that runs before the redirect. Sending an
 * already-signed-in visitor to the console regardless of why they arrived means a woman who pressed
 * *"Keep it"* on the payoff screen, in the sixty seconds after hearing her own voice for the first
 * time, reads `UNCLAIMED · CONSENT PENDING — ASR HAS NOT RUN · no rev`.
 *
 * `/voices` stays the default, because the *other* way to arrive here is pressing "Sign in" on the
 * door, and for that visitor the console is exactly the right destination.
 */
function SignInRoute() {
  const { signedIn, loading } = useSession();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? "/voices";
  if (loading) return <Blank />;
  return signedIn ? <Navigate to={from} replace /> : <SignIn />;
}

/**
 * `/status` decides for itself with `isGod` — the same predicate every admin route enforces with,
 * derived live by the server from `users.email ∈ VOX_ADMIN_EMAILS` per request (§2.3), never a build
 * flag and never an email compared in this browser.
 */
function StatusRoute() {
  const { isGod, loading } = useSession();
  if (loading) return <Blank />;
  return isGod ? <Status /> : <Navigate to="/" replace />;
}

/** Godmode stays undiscoverable to every other actor; the server repeats this check on every API. */
function AdminRoute() {
  const { isGod, loading } = useSession();
  if (loading) return <Blank />;
  return isGod ? (
    <Suspense fallback={<Blank />}>
      <Admin />
    </Suspense>
  ) : (
    <Navigate to="/" replace />
  );
}
